import * as React from "react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

export interface PhoneInputProps
  extends Omit<React.ComponentProps<"input">, "onChange"> {
  value?: string;
  onChange?: (value: string) => void;
}

/**
 * Limpia espacios en blanco y caracteres especiales excepto números y el + inicial
 */
const cleanPhoneNumber = (value: string): string => {
  if (!value) return "";
  
  // Buscar si hay un + en cualquier parte del texto
  const hasPlus = value.includes("+");
  
  // Remover todos los caracteres que no sean dígitos
  const digitsOnly = value.replace(/\D/g, "");
  
  // Retornar con + al inicio si había uno (único símbolo permitido)
  return hasPlus ? `+${digitsOnly}` : digitsOnly;
};

const PhoneInput = React.forwardRef<HTMLInputElement, PhoneInputProps>(
  ({ className, value, onChange, ...props }, ref) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const cleaned = cleanPhoneNumber(e.target.value);
      onChange?.(cleaned);
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault();
      const pastedText = e.clipboardData.getData("text");
      const cleaned = cleanPhoneNumber(pastedText);
      
      // Trigger onChange con el valor limpio
      onChange?.(cleaned);
    };

    return (
      <Input
        ref={ref}
        type="tel"
        value={value}
        onChange={handleChange}
        onPaste={handlePaste}
        className={cn(className)}
        {...props}
      />
    );
  }
);

PhoneInput.displayName = "PhoneInput";

export { PhoneInput };
